import { TradingCampaignRules } from "@cede/types";
import { CEDE_CEXS } from "@cede/utils";

/**
 * Campaign volume display constants
 */
export const CAMPAIGN_VOLUME_CONSTANTS = {
  /** Percentage threshold for showing "high progress" (green) indicator on volume bar */
  HIGH_PROGRESS_THRESHOLD: 99,
} as const;

/**
 * List of supported exchanges for trading view widget
 * TODO: use feature flags for further updates
 */
export const TRADING_VIEW_SUPPORTED_EXCHANGES = [
  CEDE_CEXS.GATEIO,
  CEDE_CEXS.MEXC,
  CEDE_CEXS.BITGET,
  CEDE_CEXS.KUCOIN,
  CEDE_CEXS.BYBIT,
  CEDE_CEXS.BINANCE,
  CEDE_CEXS.COINBASE,
  CEDE_CEXS.BITMART,
  CEDE_CEXS.KRAKEN,
  CEDE_CEXS.OKX,
  CEDE_CEXS.HTX,
  CEDE_CEXS.BITSTAMP,
  CEDE_CEXS.BITFINEX,
  CEDE_CEXS.CRYPTOCOM,
  CEDE_CEXS.BITHUMB,
];

export const RuleLabels = {
  PROPORTIONAL: "Proportional",
  FCFS: "FCFS",
  BALANCED: "Balanced",
  BALANCED_V2: "Balanced V2",
  PNL: "PnL",
  VOLUME_BATTLE: "Volume Battle",
} as const;

/**
 * Label for exclusive campaigns (accessible only to whitelisted participants via UTM)
 */
export const EXCLUSIVE_CAMPAIGN_LABEL = "Exclusive" as const;

// Type-safe mapping that ensures all TradingCampaignRules are covered
export const TradingCampaignRulesLabels: Record<TradingCampaignRules, string> = {
  [TradingCampaignRules.FIXED_PER_VOLUME]: RuleLabels.FCFS,
  [TradingCampaignRules.PRIZE_BY_SLOT]: RuleLabels.VOLUME_BATTLE,
  [TradingCampaignRules.PROPORTIONAL]: RuleLabels.PROPORTIONAL,
  [TradingCampaignRules.PROPORTIONAL_V2]: RuleLabels.PROPORTIONAL,
  [TradingCampaignRules.CUSTOM]: RuleLabels.PROPORTIONAL,
  [TradingCampaignRules.PVP]: RuleLabels.PROPORTIONAL,
  [TradingCampaignRules.STAIRS]: RuleLabels.FCFS,
  [TradingCampaignRules.SPREAD_TRADES_OVER_TIME]: RuleLabels.FCFS,
  [TradingCampaignRules.GAUSSIAN]: RuleLabels.BALANCED,
  [TradingCampaignRules.PNL]: RuleLabels.PNL,
  [TradingCampaignRules.POWER]: RuleLabels.BALANCED_V2,
} as const;

export const TradingCampaignRulesByLabels: Record<string, TradingCampaignRules[]> = {
  [RuleLabels.PROPORTIONAL]: [
    TradingCampaignRules.PROPORTIONAL,
    TradingCampaignRules.PROPORTIONAL_V2,
    TradingCampaignRules.PVP,
    TradingCampaignRules.CUSTOM,
  ],
  [RuleLabels.FCFS]: [
    TradingCampaignRules.FIXED_PER_VOLUME,
    TradingCampaignRules.STAIRS,
    TradingCampaignRules.SPREAD_TRADES_OVER_TIME,
  ],
  [RuleLabels.VOLUME_BATTLE]: [TradingCampaignRules.PRIZE_BY_SLOT],
  [RuleLabels.BALANCED]: [TradingCampaignRules.GAUSSIAN],
  [RuleLabels.PNL]: [TradingCampaignRules.PNL],
  [RuleLabels.BALANCED_V2]: [TradingCampaignRules.POWER],
};

// Type-safe campaign rule filter mapping: Display Name -> Actual Rule Types
// This ensures all rules are covered and properly mapped
export const CAMPAIGN_RULE_FILTER_MAPPING = {
  [RuleLabels.PROPORTIONAL]: [
    TradingCampaignRules.PROPORTIONAL,
    TradingCampaignRules.PROPORTIONAL_V2,
    TradingCampaignRules.PVP,
    TradingCampaignRules.CUSTOM,
  ],
  [RuleLabels.FCFS]: [
    TradingCampaignRules.FIXED_PER_VOLUME,
    TradingCampaignRules.STAIRS,
    TradingCampaignRules.SPREAD_TRADES_OVER_TIME,
  ],
  [RuleLabels.VOLUME_BATTLE]: [TradingCampaignRules.PRIZE_BY_SLOT],
  [RuleLabels.BALANCED]: [TradingCampaignRules.GAUSSIAN],
  [RuleLabels.PNL]: [TradingCampaignRules.PNL],
  [RuleLabels.BALANCED_V2]: [TradingCampaignRules.POWER],
} as const;
